// Considered: AtlasPerspectiveDetails — rejected: it is subject-first (one row per subject) with no grant,
// hidden-count or paging concepts; grouping by perspective is a different shape. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { SphereMember, SphereMemberGroup } from 'types/sphereMembers';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { CollapsibleSectionAtom } from '../../atoms/CollapsibleSection';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import MemberRowMolecule from '../MemberRow/MemberRowMolecule';
import './MemberGroupMolecule.scss';

export interface MemberGroupMoleculeProps {
  group: SphereMemberGroup;
  /** Localized perspective label (the organism resolves `labelKey` through the schema namespace). */
  label: string;
  /** Localized perspective description. */
  description?: string;
  /** Subject ids with a remove in flight. */
  removingIds?: ReadonlySet<string>;
  /** A "Show all" page is loading. */
  loadingMore?: boolean;
  /** The last "Show all" page failed. */
  loadMoreFailed?: boolean;
  /** Edit mode hides every control. */
  readOnly?: boolean;
  onAdd?: (perspectiveSystemName: string) => void;
  onRemove?: (perspectiveSystemName: string, subjectId: string) => void;
  onShowAll?: (perspectiveSystemName: string, offset: number) => void;
}

const MemberGroupMolecule: React.FC<MemberGroupMoleculeProps> = ({
  group,
  label,
  description,
  removingIds,
  loadingMore = false,
  loadMoreFailed = false,
  readOnly = false,
  onAdd,
  onRemove,
  onShowAll,
}) => {
  const { t } = useTranslation();
  const systemName = group.perspective.systemName;
  const canAdd = group.canAssign && !readOnly && !!onAdd;
  const { members } = group;

  // Propagated members render under the role that carries them. One whose role is not listed here
  // (hidden from the caller, or on a later page) renders flat and is tagged "Through a role".
  const nestedByRole = new Map<string, SphereMember[]>();
  const listedRoles = new Set(members.filter((m) => m.kind === 'role').map((m) => m.subjectId));
  const topLevel = members.filter((member) => {
    const roleId = member.viaRole?.roleId;
    if (!roleId || !listedRoles.has(roleId)) return true;
    nestedByRole.set(roleId, [...(nestedByRole.get(roleId) ?? []), member]);
    return false;
  });

  const renderRow = (member: SphereMember, nested = false): React.ReactNode => (
    <MemberRowMolecule
      key={member.subjectId}
      member={member}
      perspectiveLabel={label}
      canRemove={!readOnly && member.canRemove}
      removing={removingIds?.has(member.subjectId)}
      onRemove={onRemove ? (subjectId) => onRemove(systemName, subjectId) : undefined}
      throughRole={!nested && member.viaRole !== undefined}
    >
      {nested ? undefined : nestedByRole.get(member.subjectId)?.map((child) => renderRow(child, true))}
    </MemberRowMolecule>
  );

  return (
    <CollapsibleSectionAtom
      title={label}
      subtitle={description}
      data-testid={`member-group-${systemName}`}
    >
      <div className="member-group">
        {canAdd || (group.grants && group.grants.length > 0) ? (
          <div className="member-group__toolbar">
            {group.grants && group.grants.length > 0 ? (
              <div className="member-group__grants">
                {group.grants.map((level) => (
                  <ChipAtom key={level} label={t(`sphere.members.grants.${level}`)} />
                ))}
              </div>
            ) : null}
            {canAdd ? (
              <ButtonAtom
                type="button"
                variant="secondary"
                size="small"
                icon="pi pi-plus"
                label={t('sphere.members.add')}
                aria-label={t('sphere.members.addTo', { perspective: label })}
                onClick={() => onAdd?.(systemName)}
              />
            ) : null}
          </div>
        ) : null}
        {members.length === 0 && group.hiddenCount === 0 ? (
          <p className="member-group__empty">{t('sphere.members.noOneYet')}</p>
        ) : null}
        {members.length > 0 ? (
          <ul className="member-group__list">{topLevel.map((member) => renderRow(member))}</ul>
        ) : null}
        {loadingMore ? <SkeletonAtom count={2} height="2rem" /> : null}
        {loadMoreFailed ? (
          <p className="member-group__error" role="alert">
            {t('sphere.members.showAllFailed')}{' '}
            <ButtonAtom
              type="button"
              variant="text"
              size="small"
              label={t('common.retry')}
              onClick={() => group.nextOffset !== null && onShowAll?.(systemName, group.nextOffset)}
            />
          </p>
        ) : null}
        {group.nextOffset !== null && !loadingMore && !loadMoreFailed && onShowAll ? (
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('sphere.members.showAll', { count: group.total - group.hiddenCount })}
            onClick={() => onShowAll(systemName, group.nextOffset as number)}
          />
        ) : null}
        {group.hiddenCount > 0 ? (
          <p className="member-group__hidden">
            {t(members.length > 0 ? 'sphere.members.hiddenCount' : 'sphere.members.allHidden', {
              count: group.hiddenCount,
            })}
          </p>
        ) : null}
      </div>
    </CollapsibleSectionAtom>
  );
};

export default MemberGroupMolecule;
